import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { ActionModal, Button, DialogPosition } from '@ecdlink/ui';
import { InfantDto, MotherDto, useDialog } from '@ecdlink/core';
import { useAppDispatch } from '@/store';
import {
  CmsVisitDataInputModelInput,
  CmsVisitSectionInput,
  InputMaybe,
  VisitDataStatusFilterInput,
} from '@ecdlink/graphql';
import { visitThunkActions } from '@/store/visit';
import { referralThunkActions } from '@/store/referral';
import { useLocation, useParams, useHistory } from 'react-router';
import { activitiesTypes } from '../activities-list';
import ROUTES from '@/routes/routes';
import { ButtonProps } from '@ecdlink/ui/lib/components/button/button.types';
import { useSelector } from 'react-redux';
import {
  notificationActions,
  notificationsSelectors,
} from '@store/notifications';
import { notificationTagConfig } from '@/constants/notifications';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { getMotherStatusInfo } from '@/utils/mom/motherVisitStatus.utils';
import { motherActions } from '@/store/mother';
import { transformToVisitDataDtos } from '@/utils/visits/visitDataTransformer.util';
import {
  getVisitStepKey,
  mergeVisitSectionQuestions,
  useVisitedVisitSteps,
} from '@/utils/visits/visit-step-state.util';

export interface Question {
  question: string;
  answer:
    | string
    | string[]
    | boolean
    | boolean[]
    | (string | number | undefined)[]
    | undefined;
}

export interface SectionQuestions {
  visitSection: string;
  questions: Question[];
}

export interface DynamicFormProps {
  name?: string;
  mother?: MotherDto;
  infant?: InfantDto;
  currentStep?: number;
  isTipPage?: boolean;
  steps?: any[]; // TODO: add type
  sectionQuestions?: SectionQuestions[];
  setIsTip?: (value: boolean) => void;
  setSectionQuestions?: (value?: SectionQuestions[]) => void;
  setReferralsInput?: (value?: VisitDataStatusFilterInput[]) => void;
  setEnableButton?: (value: boolean) => void;
  setButtonProperties?: (value?: ButtonProps) => void;
  setExtraButtons?: (buttons?: JSX.Element[]) => void;
  onNextStep?: () => void;
  onPreviousStep?: () => void;
  onClose?: () => void;
  onSubmit?: () => void;
}

export interface MotherProfileParams {
  id: string;
  visitId: string;
}
export interface ViewEditState {
  editView?: true;
}

export const DynamicForm = ({
  name,
  infant,
  mother,
  currentStep,
  steps,
  isTipPage,
  setSectionQuestions: setSectionQuestionsForm,
  onNextStep,
  setIsTip,
  onClose,
}: DynamicFormProps) => {
  const [isEnableButton, setIsEnableButton] = useState(false);
  const [buttonProperties, setButtonProperties] = useState<
    ButtonProps | undefined
  >(undefined);
  const [extraButtons, setExtraButtons] = useState<JSX.Element[] | undefined>(
    undefined
  );
  const [sectionQuestions, setSectionQuestions] =
    useState<SectionQuestions[]>();
  const [referralsInput, setReferralsInput] =
    useState<VisitDataStatusFilterInput[]>();
  const history = useHistory();
  const { isOnline } = useOnlineStatus();
  const [isLoading, setIsLoading] = useState(false);

  // Use this for getting status info
  const motherVisits = mother?.visits || [];

  const location = useLocation<ViewEditState>();
  const canEdit = location.state?.editView;
  const dialog = useDialog();

  const { visitId } = useParams<MotherProfileParams>();

  const appDispatch = useAppDispatch();

  const motherAgerNotification = useSelector(
    notificationsSelectors.getAllNotifications
  ).find(
    (item) =>
      item?.message?.cta?.includes(
        notificationTagConfig?.MotherAge.cta ?? ''
      ) && item?.message?.routeConfig?.route.includes(mother?.userId!)
  );

  const { stepIndex, isRevisit, maxMountedIndex, hasVisitedStep } =
    useVisitedVisitSteps(currentStep, steps);

  const noopSetEnableButton = useCallback((_enabled: boolean) => undefined, []);
  const noopSetButtonProperties = useCallback(
    (_value?: ButtonProps) => undefined,
    []
  );
  const noopSetExtraButtons = useCallback(
    (_buttons?: JSX.Element[]) => undefined,
    []
  );

  const handleSetQuestions = useCallback(
    (value: SectionQuestions[]) => {
      setSectionQuestions((prevSections) => {
        const updatedQuestions = mergeVisitSectionQuestions(
          prevSections,
          value
        ) as SectionQuestions[];

        setSectionQuestionsForm?.(updatedQuestions);
        return updatedQuestions;
      });
    },
    [setSectionQuestionsForm]
  );

  const handleSetReferrals = useCallback(
    (value: VisitDataStatusFilterInput[]) => {
      setReferralsInput((prevState) => {
        const newState = [...(prevState || [])];
        value.forEach((item) => {
          const index = newState.findIndex((element) => element.id === item.id);
          if (index !== -1) {
            newState[index].isCompleted = item.isCompleted;
          } else {
            newState.push(item);
          }
        });
        return newState;
      });
    },
    []
  );
  const handleOnNext = useCallback(() => {
    const nextIndex = stepIndex + 1;
    setButtonProperties(undefined);
    setExtraButtons(undefined);
    setIsEnableButton(hasVisitedStep(nextIndex));
    onNextStep?.();
  }, [hasVisitedStep, onNextStep, stepIndex]);

  const onSubmit = useCallback(async () => {
    if (!canEdit) {
      return dialog({
        position: DialogPosition.Middle,
        color: 'bg-white',
        render(onClose) {
          return (
            <ActionModal
              className="z-50"
              icon="ExclamationCircleIcon"
              iconColor="alertMain"
              iconClassName="h-10 w-10"
              title="You cannot edit this form"
              detailText="You have completed this visit and can only view this information"
              actionButtons={[
                {
                  text: 'Close',
                  colour: 'primary',
                  type: 'filled',
                  textColour: 'white',
                  leadingIcon: 'XIcon',
                  onClick: onClose,
                },
              ]}
            />
          );
        },
      });
    } else {
      setIsLoading(true);
      const sections = sectionQuestions?.map((item) => ({
        ...item,
        questions: item.questions.map((question) => ({
          ...question,
          answer: String(question.answer),
        })),
      })) as InputMaybe<Array<InputMaybe<CmsVisitSectionInput>>>;

      const input: CmsVisitDataInputModelInput = {
        visitId: visitId,
        motherId: mother?.userId ?? mother?.user?.id,
        visitData: {
          visitName: name,
          sections,
        },
      };

      // Redux updates - update visitAnswers on visit
      const visitAnswers = transformToVisitDataDtos(
        input.visitData!,
        isOnline,
        visitId
      );

      // update the visit state with answers, startDate, attended and sync status
      await appDispatch(
        motherActions.updateMomVisit({
          motherId: mother?.id!,
          visitId: visitId,
          visitAnswers: visitAnswers,
          visitName: name!,
          isOnline: isOnline,
        })
      );

      // when offline, populate mother status info with data we have
      if (!isOnline) {
        const updatedStatusInfo = await getMotherStatusInfo(
          new Date(mother?.expectedDateOfDelivery!),
          motherVisits
        );

        const updatedMother = {
          ...mother,
          statusInfo: updatedStatusInfo,
        };

        await appDispatch(motherActions.updateMother(updatedMother));
      }

      // referrals are saved on the state.mothers.referralsForMother
      const referrals = referralsInput?.map((item) => ({
        ...item,
        isCompleted: item.isCompleted,
      })) as VisitDataStatusFilterInput[];

      if (isOnline) {
        // posting answers to backend
        await appDispatch(visitThunkActions.addVisitForMomFormData(input));
      }

      if (!!referrals?.length && isOnline) {
        await appDispatch(
          referralThunkActions.updateVisitDataStatus({ input: referrals })
        );
      }
      if (motherAgerNotification) {
        await appDispatch(
          notificationActions.removeNotification(motherAgerNotification!)
        );
      }
      if (name === activitiesTypes.followUp && isOnline) {
        history.push(
          `${ROUTES.CLIENTS.MOM_PROFILE.ROOT}${
            mother?.userId ?? mother?.user?.id
          }`
        );
      }
      setIsLoading(false);
      onClose?.();
    }

    if (name === activitiesTypes.followUp && isOnline) {
      history.push(
        `${ROUTES.CLIENTS.MOM_PROFILE.ROOT}${
          mother?.userId ?? mother?.user?.id
        }`
      );
    }

    onClose?.();
  }, [
    canEdit,
    name,
    dialog,
    sectionQuestions,
    visitId,
    mother?.user?.id,
    referralsInput,
    appDispatch,
    motherAgerNotification,
    history,
    motherVisits,
  ]);

  useLayoutEffect(() => {
    setButtonProperties(undefined);
    setExtraButtons(undefined);
    if (isRevisit) {
      setIsEnableButton(true);
    }
  }, [isRevisit, stepIndex]);

  const renderContent = useMemo(() => {
    if (!steps?.length) return;

    return steps.map((StepComponent, index) => {
      if (index > maxMountedIndex || !StepComponent) return null;

      const isActive = index === stepIndex;

      return (
        <div key={getVisitStepKey(StepComponent, index)} hidden={!isActive}>
          <StepComponent
            infant={infant}
            mother={mother}
            isTipPage={isActive && isTipPage}
            setIsTip={setIsTip}
            sectionQuestions={sectionQuestions}
            setSectionQuestions={handleSetQuestions}
            setReferralsInput={handleSetReferrals}
            setEnableButton={isActive ? setIsEnableButton : noopSetEnableButton}
            setButtonProperties={
              isActive ? setButtonProperties : noopSetButtonProperties
            }
            setExtraButtons={isActive ? setExtraButtons : noopSetExtraButtons}
            onNextStep={onNextStep}
            onSubmit={onSubmit}
          />
        </div>
      );
    });
  }, [
    handleSetQuestions,
    handleSetReferrals,
    infant,
    isTipPage,
    maxMountedIndex,
    mother,
    noopSetButtonProperties,
    noopSetEnableButton,
    noopSetExtraButtons,
    onNextStep,
    onSubmit,
    sectionQuestions,
    setIsTip,
    stepIndex,
    steps,
  ]);

  const renderButton = useMemo(() => {
    if (
      Number(currentStep) === 0 &&
      !(Number(currentStep) <= Number(steps?.length) - 1)
    ) {
      return {
        action: handleOnNext,
        text: 'Start',
        icon: 'ClipboardListIcon',
      };
    }

    if (Number(currentStep) < Number(steps?.length) - 1) {
      return {
        action: handleOnNext,
        text: 'Next',
        icon: 'ArrowCircleRightIcon',
      };
    }

    return {
      action: onSubmit,
      text: buttonProperties?.text || 'Save',
      icon: 'SaveIcon',
    };
  }, [currentStep, handleOnNext, onSubmit, steps?.length, buttonProperties]);

  const renderExtraButtons = useCallback(() => {
    if (!extraButtons || extraButtons.length === 0) return null;
    return extraButtons.map((el, index) => el);
  }, [extraButtons]);

  return (
    <div className="flex h-full flex-col">
      {renderContent}
      {!isTipPage && (
        <div id="button" className="mx-4 mt-auto flex flex-col items-end">
          {renderExtraButtons()}
          <Button
            type={buttonProperties?.type || 'filled'}
            color={buttonProperties?.color || 'primary'}
            textColor={buttonProperties?.textColor || 'white'}
            icon={renderButton.icon}
            className="mb-4 w-full"
            text={renderButton.text}
            onClick={renderButton.action}
            disabled={!isEnableButton || isLoading}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};
